import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { SWRConfig } from 'swr';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import api from './utils/api';
import Landing from './pages/Landing';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import BrandOnboarding from './pages/BrandOnboarding';
import StorageSetup from './pages/StorageSetup';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Orders from './pages/Orders';
import Customers from './pages/Customers';
import Financial from './pages/Financial';
import Suppliers from './pages/Suppliers';
import Returns from './pages/Returns';
import Checklist from './pages/Checklist';
import Collection from './pages/Collection';
import DriveSetup from './pages/DriveSetup';
import BrandSettings from './pages/BrandSettings';
import Help from './pages/Help';
import Layout from './components/Layout';

const swrFetcher = async (url) => {
  const res = await api.get(url);
  return res.data;
};

// ── Route Guards ──────────────────────────────────────
const ProtectedRoute = ({ children, skipStorageCheck = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg-base)' }}>
      <div className="spinner" />
    </div>
  );

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  if (!user.brand?.onboardingComplete && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  if (!skipStorageCheck && !user.storageType && location.pathname !== '/storage-setup') {
    // Only redirect to storage-setup if onboarding IS complete
    if (user.brand?.onboardingComplete) {
      return <Navigate to="/storage-setup" replace />;
    }
  }

  return children;
};

// Public-only: redirect authenticated users to dashboard
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg-base)' }}>
      <div className="spinner" />
    </div>
  );
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

// Catch-all: smart redirect based on auth state
const CatchAll = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? '/dashboard' : '/'} replace />;
};

// ── Toaster ───────────────────────────────────────────
function ToasterWithTheme() {
  return (
    <Toaster
      position="bottom-right"
      toastOptions={{
        style: {
          background: 'var(--bg-layer1)',
          color: 'var(--text-primary)',
          border: '1px solid var(--accent-border)',
          fontFamily: "var(--font-body)",
          fontSize: '0.85rem',
          borderRadius: '12px',
          boxShadow: 'var(--sha-md)',
        },
        success: { iconTheme: { primary: '#34D399', secondary: 'white' } },
        error: { iconTheme: { primary: 'var(--accent)', secondary: 'white' } },
      }}
    />
  );
}

// ── Routes ────────────────────────────────────────────
function AppRoutes() {
  return (
    <Routes>
      {/* ── Public / Unauthenticated ── */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* ── Setup Flow (requires auth, skips full protection) ── */}
      <Route path="/onboarding" element={<ProtectedRoute skipStorageCheck><BrandOnboarding /></ProtectedRoute>} />
      <Route path="/storage-setup" element={<ProtectedRoute skipStorageCheck><StorageSetup /></ProtectedRoute>} />

      {/* ── Protected App Shell ── */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/products" element={<Products />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/financial" element={<Financial />} />
        <Route path="/suppliers" element={<Suppliers />} />
        <Route path="/returns" element={<Returns />} />
        <Route path="/checklist" element={<Checklist />} />
        <Route path="/collections" element={<Collection />} />
        <Route path="/drive-setup" element={<DriveSetup />} />
        <Route path="/brand-settings" element={<BrandSettings />} />
        <Route path="/help" element={<Help />} />
      </Route>

      {/* ── Catch-All ── */}
      <Route path="*" element={<CatchAll />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <SWRConfig value={{
              fetcher: swrFetcher,
              dedupingInterval: 60 * 1000,
              revalidateOnFocus: false,
              shouldRetryOnError: true,
              errorRetryCount: 2,
            }}>
              <AppRoutes />
            </SWRConfig>
            <ToasterWithTheme />
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
